import jsPDF from 'jspdf';

/**
 * Strip HTML tags and convert to plain text for PDF
 * @param {string} html - HTML content from rich text editor
 * @returns {string} - Plain text
 */
const stripHtmlTags = (html) => {
  if (!html) return '';

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Get text content (this automatically strips tags)
  let text = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up extra whitespace
  text = text.replace(/\s+/g, ' ').trim();

  return text;
};

/**
 * Generate PDF from Treatment Plan data
 * @param {Object} treatmentPlan - The treatment plan data
 * @returns {jsPDF} - The generated PDF document
 */
export const generateTreatmentPlanPDF = async (treatmentPlan) => {
  console.log(`[Treatment Plan PDF] Generating PDF for:`, treatmentPlan);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Load and add logo
  let yPosition = 15;
  try {
    // Create a promise to load the logo image
    const logoPromise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = '/fancyTherapy.png';
    });

    const logoImg = await logoPromise;
    const logoWidth = 80;
    const logoHeight = (logoImg.height / logoImg.width) * logoWidth; // Maintain aspect ratio
    const logoX = (pageWidth - logoWidth) / 2;
    doc.addImage(logoImg, 'PNG', logoX, yPosition, logoWidth, logoHeight);
    yPosition += logoHeight + 8;
  } catch (error) {
    console.warn('[Treatment Plan PDF] Could not load logo:', error);
    yPosition = 20; // Start without logo if it fails
  }

  // Helper function to add text with word wrap
  const addText = (text, x, y, options = {}) => {
    const {
      fontSize = 10,
      fontStyle = 'normal',
      align = 'left',
      maxWidth = pageWidth - 40,
    } = options;

    doc.setFontSize(fontSize);
    doc.setFont('helvetica', fontStyle);

    if (align === 'center') {
      doc.text(text, pageWidth / 2, y, { align: 'center', maxWidth });
    } else {
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, y);
      return lines.length * (fontSize * 0.4); // Return approximate height
    }
    return fontSize * 0.4;
  };

  // Header
  addText('Speech & Language Treatment Plan', pageWidth / 2, yPosition, {
    fontSize: 16,
    fontStyle: 'bold',
    align: 'center',
  });
  yPosition += 8;

  // Use clinic address if available, otherwise use default Hearsay address
  let clinicAddress = '44 Main St. E., Milton, ON L9T 1N3 • Tel: (905) 875-3345 • Fax: (905) 875-0343';
  let clinicEmail = 'milton@hearsay.ca';

  if (treatmentPlan.clinicId) {
    const clinic = treatmentPlan.clinicId;
    console.log(`[Treatment Plan PDF] Using clinic data:`, clinic);

    if (clinic.address || clinic.phoneNumber) {
      const addressParts = [];
      if (clinic.address) addressParts.push(clinic.address);
      if (clinic.phoneNumber) addressParts.push(`Tel: ${clinic.phoneNumber}`);
      clinicAddress = addressParts.join(' • ');
    }

    if (clinic.email) {
      clinicEmail = clinic.email;
    }
  }

  addText(clinicAddress, pageWidth / 2, yPosition, {
    fontSize: 9,
    align: 'center',
  });
  yPosition += 5;

  addText(`e-mail: ${clinicEmail}`, pageWidth / 2, yPosition, {
    fontSize: 9,
    align: 'center',
  });
  yPosition += 10;

  // Add a line separator
  doc.setLineWidth(0.5);
  doc.line(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 8;

  // Basic Information Section
  const formatDate = (date) => {
    if (!date) return 'N/A';
    // Parse date string and use UTC to avoid timezone offset issues
    const dateStr = typeof date === 'string' ? date.split('T')[0] : date.toISOString().split('T')[0];
    const [year, month, day] = dateStr.split('-');
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
  };

  // Client Information
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const leftColumn = 20;
  const rightColumn = 110;
  const lineHeight = 6;

  // Create table header for client info
  doc.text(`Client Name: ${treatmentPlan.clientName || 'N/A'}`, leftColumn, yPosition);
  doc.text(`Date: ${formatDate(treatmentPlan.date)}`, rightColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`D.O.B.: ${formatDate(treatmentPlan.dateOfBirth)}`, leftColumn, yPosition);
  doc.text(`C.A.: ${treatmentPlan.chronologicalAge || 'N/A'}`, rightColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`Grade: ${treatmentPlan.grade || 'N/A'}`, leftColumn, yPosition);
  doc.text(`School: ${treatmentPlan.school || 'N/A'}`, rightColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`Speech-Language Pathologist: ${treatmentPlan.speechLanguagePathologist || 'N/A'}`, leftColumn, yPosition);
  yPosition += lineHeight;

  doc.text(`Clinician(s): ${treatmentPlan.clinicians || 'N/A'}`, leftColumn, yPosition);
  yPosition += lineHeight + 2;

  // Check if we need a new page
  if (yPosition > 220) {
    doc.addPage();
    yPosition = 20;
  }

  // Current Communication Status
  if (treatmentPlan.currentCommunicationStatus) {
    doc.setFont('helvetica', 'bold');
    doc.text('Current Communication Status:', leftColumn, yPosition);
    yPosition += 5;

    doc.setFont('helvetica', 'normal');
    const statusText = stripHtmlTags(treatmentPlan.currentCommunicationStatus);
    const statusHeight = addText(
      statusText,
      leftColumn,
      yPosition,
      { maxWidth: pageWidth - 40 }
    );
    yPosition += statusHeight + 6;
  }

  // Check if we need a new page
  if (yPosition > 220) {
    doc.addPage();
    yPosition = 20;
  }

  // Treatment Plan
  if (treatmentPlan.treatmentPlan) {
    doc.setFont('helvetica', 'bold');
    doc.text('Treatment Plan:', leftColumn, yPosition);
    yPosition += 5;

    doc.setFont('helvetica', 'normal');
    const planText = stripHtmlTags(treatmentPlan.treatmentPlan);
    const planHeight = addText(
      planText,
      leftColumn,
      yPosition,
      { maxWidth: pageWidth - 40 }
    );
    yPosition += planHeight + 6;
  }

  // Check if we need a new page before the goals
  if (yPosition > 220) {
    doc.addPage();
    yPosition = 20;
  }

  // Treatment Goals
  doc.setFont('helvetica', 'bold');
  doc.text('Treatment Goals', leftColumn, yPosition);
  yPosition += 5;

  if (treatmentPlan.goals && treatmentPlan.goals.length > 0) {
    doc.setFont('helvetica', 'normal');

    treatmentPlan.goals.forEach((goal, index) => {
      // Check if we need a new page for this goal
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      // Goal number - ALWAYS use array index + 1 for sequential numbering (1, 2, 3, 4...)
      // Never use goal.id as it may contain timestamp values from Date.now()
      const goalNumber = index + 1;
      doc.setFont('helvetica', 'bold');
      doc.text(`${goalNumber}.`, leftColumn, yPosition);

      // Goal description
      doc.setFont('helvetica', 'normal');
      const goalText = stripHtmlTags(goal.description);
      const goalHeight = addText(
        goalText,
        leftColumn + 5,
        yPosition,
        { maxWidth: pageWidth - 45 }
      );
      yPosition += Math.max(goalHeight, 6) + 3;
    });
  } else {
    doc.setFont('helvetica', 'normal');
    doc.text('No goals specified', leftColumn, yPosition);
    yPosition += lineHeight;
  }

  yPosition += 8;

  // Footer - Signature Section
  if (yPosition > 240) {
    doc.addPage();
    yPosition = 20;
  }

  yPosition += 5;
  doc.setLineWidth(0.3);
  doc.line(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 6;

  // Display speech-language pathologist if provided
  if (treatmentPlan.speechLanguagePathologist) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');

    // Determine qualifications to display
    let qualifications = treatmentPlan.speechLanguagePathologistQualifications;

    // If qualifications not stored in treatment plan, look up from populated user data
    if (!qualifications && treatmentPlan.speechLanguagePathologistId) {
      qualifications = treatmentPlan.speechLanguagePathologistId.invoiceDescriptor;
      console.log(`[Treatment Plan PDF] Looking up qualifications from user: ${qualifications}`);
    }

    // Use found qualifications or default to "Reg. CASLPO"
    const slpText = qualifications
      ? `${treatmentPlan.speechLanguagePathologist}, ${qualifications}`
      : `${treatmentPlan.speechLanguagePathologist}, Reg. CASLPO`;

    addText(slpText, pageWidth / 2, yPosition, {
      fontSize: 9,
      fontStyle: 'bold',
      align: 'center',
    });
    yPosition += 4;

    doc.setFont('helvetica', 'normal');
    addText('Speech-Language Pathologist', pageWidth / 2, yPosition, {
      fontSize: 9,
      align: 'center',
    });
    yPosition += 6;
  }

  // Add "c.: parent(s), client file"
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('c.: parent(s), client file', leftColumn, yPosition);
  yPosition += 6;

  // Add date of report
  doc.text(`(date of report/ ${formatDate(treatmentPlan.date)})`, leftColumn, yPosition);

  return doc;
};

/**
 * Download Treatment Plan as PDF
 * @param {Object} treatmentPlan - The treatment plan data
 * @param {string} filename - Optional custom filename
 */
export const downloadTreatmentPlanPDF = async (treatmentPlan, filename) => {
  const doc = await generateTreatmentPlanPDF(treatmentPlan);
  const defaultFilename = filename || `treatment-plan-${treatmentPlan.clientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(defaultFilename);
  console.log(`[Treatment Plan PDF] Downloaded: ${defaultFilename}`);
};
